import { RefObject, useLayoutEffect, useState } from 'react';

// Ignore sub-pixel height changes so they don't trigger renders.
const HEIGHT_CHANGE_TOLERANCE_PX = 1;

/** rAF-coalesced ResizeObserver of an element's border-box height, in pixels. */
export function useElementHeight(ref: RefObject<HTMLElement | null>): number {
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el == null || typeof ResizeObserver === 'undefined') {
      return;
    }

    // Seed synchronously so the first render has a real height, not 0.
    setHeight(el.getBoundingClientRect().height);

    let pending = el.getBoundingClientRect().height;
    let rafId: number | null = null;

    const flush = () => {
      rafId = null;
      setHeight((prev) =>
        Math.abs(prev - pending) >= HEIGHT_CHANGE_TOLERANCE_PX ? pending : prev,
      );
    };

    const ro = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry == null) {
        return;
      }
      // Prefer border-box height (includes padding + border, excludes margin).
      const box = entry.borderBoxSize?.[0];
      pending = box != null ? box.blockSize : entry.contentRect.height;
      if (rafId == null) {
        rafId = requestAnimationFrame(flush);
      }
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      if (rafId != null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [ref]);

  return height;
}
